<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NDropdown, NModal, type DropdownOption } from 'naive-ui'
import AgentRepoDevcontainerActions from './AgentRepoDevcontainerActions.vue'
import AgentRepoContainerWorkspace from './AgentRepoContainerWorkspace.vue'
import { useAgentReposStore, type AgentRepoStatus } from '../stores/agent_repos'
import { useAsyncAction } from '../composables/useAsyncAction'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'

// Kebab menu for a repo card's rarer actions (#1071) — devcontainer
// lifecycle controls and the container-workspace override move behind
// "Advanced settings…" so the card's default view isn't dominated by
// buttons an admin touches occasionally, while Set as default / Remove
// stay one click away as plain menu entries.
const props = defineProps<{ repo: AgentRepoStatus }>()
const store = useAgentReposStore()

type MenuKey = 'advanced' | 'refresh-stats' | 'set-default' | 'remove'

const menuOptions = computed<DropdownOption[]>(() => {
  const options: DropdownOption[] = [
    { label: 'Advanced settings…', key: 'advanced' satisfies MenuKey },
    { label: 'Refresh metrics', key: 'refresh-stats' satisfies MenuKey },
  ]
  if (!props.repo.is_default) options.push({ label: 'Set as default', key: 'set-default' satisfies MenuKey })
  // Divider so the destructive entry doesn't sit flush against routine ones.
  if (props.repo.source === 'db')
    options.push({ type: 'divider', key: 'remove-divider' }, { label: 'Remove', key: 'remove' satisfies MenuKey })
  return options
})

const advancedOpen = ref(false)
useRestoreFocusOnClose(advancedOpen)

const removeConfirmOpen = ref(false)
useRestoreFocusOnClose(removeConfirmOpen)

function onSelect(key: string): void {
  switch (key as MenuKey) {
    case 'advanced':
      advancedOpen.value = true
      break
    case 'refresh-stats':
      refreshStats()
      break
    case 'set-default':
      setDefault()
      break
    case 'remove':
      removeConfirmOpen.value = true
      break
  }
}

// The agent box measures these on its own schedule, so the success message
// says "queued" rather than implying the numbers on screen have already moved
// — they land on the page's next status poll.
const { run: refreshStats } = useAsyncAction(() => store.refreshStats(props.repo.slug), {
  successMessage: () => `Re-measuring ${props.repo.slug}…`,
  failureLabel: 'Failed to refresh metrics',
})

const { run: setDefault } = useAsyncAction(() => store.update(props.repo.slug, { is_default: true }), {
  successMessage: () => `${props.repo.slug} set as default`,
  failureLabel: 'Failed to set default',
})

const { run: remove } = useAsyncAction(() => store.destroy(props.repo.slug), {
  successMessage: () => `${props.repo.slug} removed`,
  failureLabel: 'Failed to remove repo',
})

function confirmRemove(): void {
  removeConfirmOpen.value = false
  remove()
}
</script>

<template>
  <NDropdown trigger="click" :options="menuOptions" @select="onSelect">
    <NButton size="small" quaternary circle aria-label="Repo actions" :data-testid="`agent-repo-menu-${repo.slug}`"
      >⋮</NButton
    >
  </NDropdown>

  <NModal
    v-model:show="advancedOpen"
    preset="card"
    title="Advanced settings"
    style="width: 480px"
    :data-testid="`agent-repo-advanced-modal-${repo.slug}`"
  >
    <div class="flex flex-col gap-3">
      <AgentRepoContainerWorkspace :repo="repo" />
      <div class="flex flex-wrap items-center gap-2 border-t border-border-divider pt-3">
        <AgentRepoDevcontainerActions :repo="repo" />
      </div>
    </div>
  </NModal>

  <!-- NModal preset="dialog" rather than NPopconfirm — the popconfirm
       anchors to its trigger, which the dropdown has already unmounted by
       the time this fires (same reasoning as WikiPageMeta.vue's mobile
       delete confirm). Self-mounts via the global teleport, no
       NDialogProvider needed at the app root. -->
  <NModal
    v-model:show="removeConfirmOpen"
    preset="dialog"
    type="error"
    title="Remove repo"
    positive-text="Remove"
    negative-text="Cancel"
    :positive-button-props="{ type: 'error' }"
    :data-testid="`agent-repo-remove-confirm-${repo.slug}`"
    @positive-click="confirmRemove"
  >
    Remove <strong>{{ repo.slug }}</strong> from the DB? The YAML fallback still applies if the slug remains in
    <code>config/agent_repos.yml</code>.
  </NModal>
</template>
